import React, {lazy, Suspense} from 'react';
import {IWidgetResponse} from '../../api/match-center';
import {getMeta, WidgetMetaTypes} from '../../utils/widget-meta';
import {BuilderComponentSlug, IBuilderComponentGroup, IWidgetBuilderComponent, oddsFormats} from '../../utils/types';
import bemnames from "../../utils/bemnames";
import {_t} from '../../utils/translations';
import LoadingBlock from "../LoadingBlock";
import {componentEventType} from "../../utils/builder-components";

const bem = bemnames.create('builder-components-list');

interface IProps {
  builderComponentGroups: IBuilderComponentGroup[];
  eventID: number;
  isMobile: boolean;
  widget: IWidgetResponse;
  oddsFormat?: oddsFormats | null;
  containerWidth: number;
}

const EventTopBanner = lazy(() => import('../../components/MatchCenter/EventTopBanner'));
const EventDetails = lazy(() => import('../../components/MatchCenter/EventDetails'));
const Head2Head = lazy(() => import('../../components/MatchCenter/Head2Head'));
const Standings = lazy(() => import('../../components/Standings/Standings'));
const TopScorers = lazy(() => import('../../components/MatchCenter/TopScorers'));
const EventOddsComparisonTable = lazy(() => import('../../components/MatchCenter/EventOddsComparisonTable'));
const MatchStatistics = lazy(() => import('../../components/MatchCenter/MatchStatistics'));
const Trends = lazy(() => import('../../components/MatchCenter/Trends/Trends'));
const AdBanner = lazy(() => import('../../components/MatchCenter/AdBanner'));
const InjuriesAndSuspensions = lazy(() => import('../../components/MatchCenter/InjuriesAndSuspensions'));
const AxSemantics = lazy(() => import('../../components/MatchCenter/AxSemantics'));
const ChannelsComponent = lazy(() => import('../MatchCenter/ChannelsComponent'));
const FeaturedEvents = lazy(() => import('../FeaturedEvents'));
const MatchPrediction = lazy(() => import('../MatchCenter/MatchPrediction'));
const NextRoundBanner = lazy(() => import('../NextRoundBanner'));
const FootballPitch = lazy(() => import('../Lineups/FootballPitch'));
const CompetitorsList = lazy(() => import('../Lineups/CompetitorsList'));
const Content = lazy(() => import('../MatchCenter/Content'));
const BuilderComponentContent = lazy(() => import('../MatchCenter/BuilderComponentContent'));
const GameMinutes = lazy(() => import('../MatchCenter/GameMinutes'));

const BuilderComponentList: React.FC<IProps> = (props) => {
  const {
    builderComponentGroups,
    isMobile,
    eventID,
    widget,
    containerWidth,
    oddsFormat = undefined
  } = props;
  const showLinks = (): boolean => getMeta(WidgetMetaTypes.matchCenterBannerLinks) === 'show_links'

  const renderBuilderComponent = (widgetBuilderComponent: IWidgetBuilderComponent) => {
    switch (widgetBuilderComponent.builderComponent.slug) {
      case BuilderComponentSlug.gameMinutes:
        return (
          <div
            key={widgetBuilderComponent.builderComponent.id}
            id={widgetBuilderComponent.builderComponent.slug}
            className={bem.e('item')}
          >
            <Suspense fallback={<LoadingBlock height={365} heightMobile={296} isMobile={isMobile}/>}>
              <GameMinutes
                isMobile={isMobile}
                eventId={eventID}
                widgetId={widget.id}
              />
            </Suspense>
          </div>
        )
      case BuilderComponentSlug.matchPrediction:
        return (
          <div
            key={widgetBuilderComponent.builderComponent.id}
            id={widgetBuilderComponent.builderComponent.slug}
            className={bem.e('item')}
          >
            <Suspense fallback={<LoadingBlock height={365} heightMobile={296} isMobile={isMobile}/>}>
              <MatchPrediction
                componentName={widgetBuilderComponent.customName ?? _t(widgetBuilderComponent.builderComponent.name)}
                isMobile={isMobile}
                eventId={eventID}
                widgetId={widget.id}
                oddsFormat={oddsFormat ?? widget.oddsFormat}
                outLinkFolder={widget.outLinks.outLinkFolder}
              />
            </Suspense>
          </div>
        )
      case BuilderComponentSlug.eventBanner:
        return (
          <div
            key={widgetBuilderComponent.builderComponent.id}
            id={widgetBuilderComponent.builderComponent.slug}
            className={bem.e('item')}
          >
            <Suspense fallback={<LoadingBlock height={365} heightMobile={296} isMobile={isMobile}/>}>
              <EventTopBanner
                componentName={widgetBuilderComponent.customName ?? _t(widgetBuilderComponent.builderComponent.name)}
                eventID={eventID}
                languageID={widget.languageId}
                allowedProviders={widget.widgetProviders}
                outLinks={widget.outLinks}
                oddsFormat={oddsFormat ?? widget.oddsFormat}
                isMobile={isMobile}
                showLinks={showLinks()}
                backgrounds={widget.backgrounds}
                widgetId={widget.id}
                builderComponentId={widgetBuilderComponent.builderComponent.id}
              />
            </Suspense>
          </div>
        );
      case BuilderComponentSlug.eventDetails:
        return (
          <div
            key={widgetBuilderComponent.builderComponent.id}
            id={widgetBuilderComponent.builderComponent.slug}
            className={bem.e('item')}
          >
            <Suspense fallback={<LoadingBlock height={365} heightMobile={296} isMobile={isMobile}/>}>
              <EventDetails
                componentName={widgetBuilderComponent.customName ?? _t(widgetBuilderComponent.builderComponent.name)}
                eventID={eventID}
                languageID={widget.languageId}
                widgetID={widget.id}
                isMobile={isMobile}
              />
            </Suspense>
          </div>
        );
      case BuilderComponentSlug.head2Head:
        return (
          <div
            key={widgetBuilderComponent.builderComponent.id}
            id={widgetBuilderComponent.builderComponent.slug}
            className={bem.e('item')}
          >
            <Suspense fallback={<LoadingBlock height={365} heightMobile={296} isMobile={isMobile}/>}>
              <Head2Head
                componentName={widgetBuilderComponent.customName ?? _t(widgetBuilderComponent.builderComponent.name)}
                eventID={eventID}
                languageID={widget.languageId}
                isMobile={isMobile}
              />
            </Suspense>
          </div>
        );
      case BuilderComponentSlug.lineupsPitch:
        return (
          <div
            key={widgetBuilderComponent.builderComponent.id}
            id={widgetBuilderComponent.builderComponent.slug}
            className={bem.e('item')}
          >
            <Suspense fallback={<LoadingBlock height={365} heightMobile={296} isMobile={isMobile}/>}>
              <FootballPitch
                componentName={widgetBuilderComponent.customName ?? _t(widgetBuilderComponent.builderComponent.name)}
                eventID={eventID}
                languageID={widget.languageId}
                isMobile={isMobile}
              />
            </Suspense>
          </div>
        )
      case BuilderComponentSlug.lineupsTable:
        return (
          <div
            key={widgetBuilderComponent.builderComponent.id}
            id={widgetBuilderComponent.builderComponent.slug}
            className={bem.e('item')}
          >
            <Suspense fallback={<LoadingBlock height={365} heightMobile={296} isMobile={isMobile}/>}>
              <CompetitorsList
                componentName={_t(widgetBuilderComponent.builderComponent.name)}
                customComponentName={widgetBuilderComponent.customName}
                isMobile={isMobile}
                oddsFormat={widget.oddsFormat}
                widgetId={widget.id}
                outLinks={widget.outLinks}
                builderComponentId={widgetBuilderComponent.builderComponent.id}
                eventId={eventID}
              />
            </Suspense>
          </div>
        )
      case BuilderComponentSlug.standings:
        return (
          <div
            key={widgetBuilderComponent.builderComponent.id}
            id={widgetBuilderComponent.builderComponent.slug}
            className={bem.e('item')}
          >
            <Suspense fallback={<LoadingBlock height={365} heightMobile={296} isMobile={isMobile}/>}>
              <Standings
                componentName={widgetBuilderComponent.customName ?? _t(widgetBuilderComponent.builderComponent.name)}
                languageID={widget.languageId}
                eventID={eventID}
                isMobile={isMobile}
                competitionId={widget.widgetCompetitions.length > 0 ? widget.widgetCompetitions[0].competitionId : null}
                tournaments={widget?.tournaments}
                oddsFormat={oddsFormat}
                widgetId={widget.id}
                outLinkFolder={widget.outLinks.channelOutLinkFolder}
                containerWidth={containerWidth}
              />
            </Suspense>
          </div>
        )
      case BuilderComponentSlug.topScorers:
        return (
          <div
            key={widgetBuilderComponent.builderComponent.id}
            id={widgetBuilderComponent.builderComponent.slug}
            className={bem.e('item')}
          >
            <Suspense fallback={<LoadingBlock height={365} heightMobile={296} isMobile={isMobile}/>}>
              <TopScorers
                componentName={widgetBuilderComponent.customName ?? _t(widgetBuilderComponent.builderComponent.name)}
                isMobile={isMobile}
                languageID={widget.languageId}
                eventID={eventID}
              />
            </Suspense>
          </div>
        )
      case BuilderComponentSlug.matchStatistics:
        return (
          <div
            key={widgetBuilderComponent.builderComponent.id}
            id={widgetBuilderComponent.builderComponent.slug}
            className={bem.e('item')}
          >
            <Suspense fallback={<LoadingBlock height={365} heightMobile={296} isMobile={isMobile}/>}>
              <MatchStatistics
                componentName={widgetBuilderComponent.customName ?? _t(widgetBuilderComponent.builderComponent.name)}
                languageID={widget.languageId}
                eventID={eventID}
                isMobile={isMobile}
              />
            </Suspense>
          </div>
        )
      case BuilderComponentSlug.oddsComparison:
        return (
          <div
            key={widgetBuilderComponent.builderComponent.id}
            id={widgetBuilderComponent.builderComponent.slug}
            className={bem.e('item')}
          >
            <Suspense fallback={<LoadingBlock height={365} heightMobile={296} isMobile={isMobile}/>}>
              <EventOddsComparisonTable
                componentName={widgetBuilderComponent.customName ?? _t(widgetBuilderComponent.builderComponent.name)}
                widget={widget}
                isMobile={isMobile}
                eventID={eventID}
                languageID={widget.languageId}
                showLinks={showLinks()}
                outLinks={widget.outLinks}
                oddsFormat={oddsFormat ?? widget.oddsFormat}
                builderComponentId={widgetBuilderComponent.builderComponent.id}
              />
            </Suspense>
          </div>
        )
      case BuilderComponentSlug.trends:
        return (
          <div
            key={widgetBuilderComponent.builderComponent.id}
            id={widgetBuilderComponent.builderComponent.slug}
            className={bem.e('item')}
          >
            <Suspense fallback={<LoadingBlock height={365} heightMobile={296} isMobile={isMobile}/>}>
              <Trends
                componentName={widgetBuilderComponent.customName ?? _t(widgetBuilderComponent.builderComponent.name)}
                widget={widget}
                widgetId={widget.id}
                eventId={eventID}
                languageId={widget.languageId}
                oddsFormat={oddsFormat ?? widget.oddsFormat}
                isMobile={isMobile}
                builderComponentId={widgetBuilderComponent.builderComponent.id}
              />
            </Suspense>
          </div>
        )
      case BuilderComponentSlug.adBanner:
        return (
          <div
            key={widgetBuilderComponent.id}
            id={widgetBuilderComponent.builderComponent.slug}
            className={bem.e('item')}
          >
            <Suspense fallback={<LoadingBlock height={365} heightMobile={296} isMobile={isMobile}/>}>
              <AdBanner
                bannerInfo={widgetBuilderComponent.widgetBanner}
                isMobile={isMobile}
              />
            </Suspense>
          </div>
        )
      case BuilderComponentSlug.injuriesAndSuspensions:
        return (
          <div
            key={widgetBuilderComponent.id}
            id={widgetBuilderComponent.builderComponent.slug}
            className={bem.e('item')}
          >
            <Suspense fallback={<LoadingBlock height={365} heightMobile={296} isMobile={isMobile}/>}>
              <InjuriesAndSuspensions
                componentName={widgetBuilderComponent.customName ?? _t(widgetBuilderComponent.builderComponent.name)}
                eventID={eventID}
                languageID={widget.languageId}
                isMobile={isMobile}
              />
            </Suspense>
          </div>
        )
      case BuilderComponentSlug.axSemantics:
        return (
          <div
            key={widgetBuilderComponent.id}
            id={widgetBuilderComponent.builderComponent.slug}
            className={bem.e('item')}
          >
            <Suspense fallback={<LoadingBlock height={365} heightMobile={296} isMobile={isMobile}/>}>
              <AxSemantics
                componentName={widgetBuilderComponent.customName ?? _t(widgetBuilderComponent.builderComponent.name)}
                eventID={eventID}
                isMobile={isMobile}
              />
            </Suspense>
          </div>
        )
      case BuilderComponentSlug.channels:
        return (
          <div
            key={widgetBuilderComponent.id}
            id={widgetBuilderComponent.builderComponent.slug}
            className={bem.e('item no-bg')}
          >
            <Suspense fallback={<LoadingBlock height={365} heightMobile={296} isMobile={isMobile}/>}>
              <ChannelsComponent
                eventID={eventID}
                isMobile={isMobile}
                widgetId={widget.id}
              />
            </Suspense>
          </div>
        )
      case BuilderComponentSlug.featuredEvents:
        return (
          <div
            key={widgetBuilderComponent.id}
            id={widgetBuilderComponent.builderComponent.slug}
            className={bem.e('item')}
          >
            <Suspense fallback={<LoadingBlock height={365} heightMobile={296} isMobile={isMobile}/>}>
              <FeaturedEvents
                componentName={widgetBuilderComponent.customName ?? _t(widgetBuilderComponent.builderComponent.name)}
                eventID={eventID}
                isMobile={isMobile}
                languageID={widget.languageId}
                oddsFormat={oddsFormat ?? widget.oddsFormat}
                widgetId={widget.id}
                builderComponentId={widgetBuilderComponent.builderComponent.id}
                outLinks={widget.outLinks}
                allowedProviders={widget.widgetProviders}
                linkTemplate={widget.linkTemplate}
                isLeagueCenter={false}
              />
            </Suspense>
          </div>
        )
      case BuilderComponentSlug.nextRoundBanner:
        return (
          <div
            key={widgetBuilderComponent.id}
            id={widgetBuilderComponent.builderComponent.slug}
            className={bem.e('item')}
          >
            <Suspense fallback={<LoadingBlock height={365} heightMobile={296} isMobile={isMobile}/>}>
              <NextRoundBanner
                outLinkFolder={widget.channelOutLinkFolder}
                isMobile={isMobile}
                widgetId={widget.id}
                eventId={eventID}
                widgetTypeId={widget.widgetTypeId}
                builderComponentId={widgetBuilderComponent.builderComponent.id}
                componentName={widgetBuilderComponent.customName ?? _t(widgetBuilderComponent.builderComponent.name)}
              />
            </Suspense>
          </div>
        )
      case BuilderComponentSlug.complianceText:
        const complianceTextMeta = getMeta(WidgetMetaTypes.complianceText)
        return (
          <div
            dangerouslySetInnerHTML={{__html: complianceTextMeta ?? ''}}
            key={widgetBuilderComponent.id}
            id={widgetBuilderComponent.builderComponent.slug}
            className={bem.e('compliance-text')}
          />
        )
      case BuilderComponentSlug.ctaButton:
        const ctaButtonText = getMeta(WidgetMetaTypes.ctaButtonText)
        const ctaButtonLink = getMeta(WidgetMetaTypes.ctaButtonLink)
        return (
          ctaButtonText && ctaButtonLink &&
          <div
            className={bem.e('cta-button')}
          >
            <a
              href={ctaButtonLink}
              target="_blank"
              rel="noreferrer"
              key={widgetBuilderComponent.id}
            >{ctaButtonText}</a>
          </div>
        )
      case BuilderComponentSlug.contentOddsPreview:
      case BuilderComponentSlug.contentBettingTips:
      case BuilderComponentSlug.contentForm:
      case BuilderComponentSlug.contentFaq:
        return (
          <div
            key={widgetBuilderComponent.id}
            id={widgetBuilderComponent.builderComponent.slug}
            className={bem.e('item')}
          >
            <Suspense fallback={<LoadingBlock height={365} heightMobile={296} isMobile={isMobile}/>}>
              <Content
                key={widgetBuilderComponent.id}
                contentTypeSlug={componentEventType[widgetBuilderComponent.builderComponent.slug]}
                isMobile={isMobile}
              />
            </Suspense>
          </div>
        )
      case BuilderComponentSlug.genericEventContent:
        return widgetBuilderComponent.builderComponent.content &&
          <Suspense fallback={<LoadingBlock height={365} heightMobile={296} isMobile={isMobile}/>}>
            <BuilderComponentContent eventContent={widgetBuilderComponent.builderComponent.content}/>
          </Suspense>
    }
  }

  return (
    <>
      {builderComponentGroups.map(builderComponentGroup => (
        <div key={builderComponentGroup.id} className={bem.e('wrapper')}>
          {builderComponentGroup.widgetBuilderComponent.map((widgetBuilderComponent: IWidgetBuilderComponent) => (
            <React.Fragment key={widgetBuilderComponent.id}>
              {renderBuilderComponent(widgetBuilderComponent)}
            </React.Fragment>
          ))}
        </div>
      ))}
    </>
  )
}

export default BuilderComponentList;
